/**
 * Unified Exports Hub — request, monitor, download, share, and regenerate
 * exports across report types and formats. Mobile-first.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import {
  createExport, listExports, downloadExport, regenerateExport, shareExport, deleteExport,
} from "@/lib/exports.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import {
  AlertTriangle, Download, FileSpreadsheet, FileText, Loader2, Mail, Plus,
  RefreshCw, Share2, ShieldCheck, Trash2, Clock, Check, Filter,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/exports")({
  component: ExportsHub,
  head: () => ({
    meta: [
      { title: "Exports — MissionReady" },
      { name: "description", content: "Generate, download, share, and manage report exports across the platform." },
    ],
  }),
});

const KIND_OPTIONS = [
  { value: "incidents", label: "Incident reports", icon: AlertTriangle },
  { value: "evidence_bundle", label: "Evidence bundle", icon: ShieldCheck },
  { value: "patrols", label: "Patrol activity", icon: Clock },
  { value: "shifts", label: "Shift summaries", icon: FileText },
  { value: "officer_logs", label: "Officer logs", icon: FileText },
  { value: "checkpoints", label: "Checkpoint activity", icon: Check },
  { value: "sla_breaches", label: "SLA breaches", icon: AlertTriangle },
  { value: "intelligence", label: "Patrol intelligence", icon: ShieldCheck },
  { value: "playback", label: "Playback summaries", icon: Clock },
] as const;

const FORMAT_OPTIONS = [
  { value: "pdf", label: "PDF", desc: "Branded, print-ready" },
  { value: "csv", label: "CSV", desc: "Raw data" },
  { value: "xlsx", label: "Excel", desc: "Spreadsheet-friendly" },
  { value: "print", label: "Print", desc: "Print-friendly PDF" },
] as const;

type Site = { id: string; name: string };

function ExportsHub() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const qc = useQueryClient();

  const list = useServerFn(listExports);
  const create = useServerFn(createExport);
  const dl = useServerFn(downloadExport);
  const regen = useServerFn(regenerateExport);
  const share = useServerFn(shareExport);
  const del = useServerFn(deleteExport);

  const [sites, setSites] = useState<Site[]>([]);
  useEffect(() => {
    if (!orgId) return;
    void supabase.from("sites").select("id,name").eq("organization_id", orgId)
      .then(({ data }) => setSites((data as Site[]) ?? []));
  }, [orgId]);

  const exports = useQuery({
    queryKey: ["exports", orgId],
    queryFn: () => list({ data: { organizationId: orgId! } }),
    enabled: !!orgId,
    refetchInterval: (q) =>
      ((q.state.data as any[])?.some?.((j) => j.status === "processing" || j.status === "queued")) ? 3000 : false,
  });

  const [openCreate, setOpenCreate] = useState(false);
  const [kind, setKind] = useState<string>("incidents");
  const [format, setFormat] = useState<string>("pdf");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [siteId, setSiteId] = useState<string>("all");
  const [bulk, setBulk] = useState(false);

  const createMutation = useMutation({
    mutationFn: () =>
      create({
        data: {
          organizationId: orgId!,
          kind: kind as any,
          format: format as any,
          filters: {
            from: from || undefined,
            to: to || undefined,
            siteIds: siteId !== "all" ? [siteId] : undefined,
          },
        },
      }),
    onSuccess: () => {
      toast.success("Export ready");
      setOpenCreate(false);
      qc.invalidateQueries({ queryKey: ["exports", orgId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to generate"),
  });

  const onDownload = async (id: string) => {
    try {
      const { url, filename } = await dl({ data: { id } });
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e: any) { toast.error(e?.message ?? "Download failed"); }
  };

  const onRegenerate = async (id: string) => {
    try {
      await regen({ data: { id } });
      toast.success("Regenerated");
      qc.invalidateQueries({ queryKey: ["exports", orgId] });
    } catch (e: any) { toast.error(e?.message ?? "Regenerate failed"); }
  };

  const onDelete = async (id: string) => {
    if (!confirm("Delete this export?")) return;
    try {
      await del({ data: { id } });
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["exports", orgId] });
    } catch (e: any) { toast.error(e?.message ?? "Delete failed"); }
  };

  const onShare = async (id: string, recipients: string[]) => {
    try {
      await share({ data: { id, recipients } });
      toast.success(`Shared with ${recipients.length} recipient(s)`);
      qc.invalidateQueries({ queryKey: ["exports", orgId] });
    } catch (e: any) { toast.error(e?.message ?? "Share failed"); }
  };

  const rows = (exports.data as any[]) ?? [];
  const grouped = useMemo(() => {
    const out = { active: [] as any[], history: [] as any[] };
    for (const r of rows) {
      if (r.status === "queued" || r.status === "processing") out.active.push(r);
      else out.history.push(r);
    }
    return out;
  }, [rows]);

  return (
    <div className="px-4 pb-28 pt-6 md:px-8 md:py-8 md:pb-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Operations Center</p>
          <h1 className="font-display text-2xl font-bold md:text-3xl">Exports</h1>
          <p className="text-sm text-muted-foreground">
            Generate PDF, CSV, and Excel exports for incidents, patrols, shifts, and more.
          </p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button size="sm" className="hidden sm:inline-flex">
              <Plus className="mr-1 h-4 w-4" /> New export
            </Button>
          </DialogTrigger>
          <CreateExportDialog
            kind={kind} setKind={setKind}
            format={format} setFormat={setFormat}
            from={from} setFrom={setFrom}
            to={to} setTo={setTo}
            siteId={siteId} setSiteId={setSiteId}
            sites={sites}
            bulk={bulk} setBulk={setBulk}
            onSubmit={() => createMutation.mutate()}
            pending={createMutation.isPending}
          />
        </Dialog>
      </div>

      {grouped.active.length > 0 && (
        <Card className="mt-5 border-primary/30 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              Generating ({grouped.active.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {grouped.active.map((j) => (
              <div key={j.id} className="flex items-center justify-between rounded-md bg-background/60 px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate font-medium">{titleFor(j.kind)}</div>
                  <div className="text-xs text-muted-foreground">{j.format.toUpperCase()} • {new Date(j.created_at).toLocaleTimeString()}</div>
                </div>
                <Badge variant="outline" className="capitalize">{j.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="mt-5">
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">Export history</CardTitle>
          <Button size="sm" variant="outline" onClick={() => exports.refetch()}>
            <RefreshCw className={`mr-1 h-4 w-4 ${exports.isFetching ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {exports.isLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
          ) : grouped.history.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <FileSpreadsheet className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
              No exports yet. Tap “New export” to generate one.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {grouped.history.map((j) => (
                <ExportRow key={j.id} job={j}
                  onDownload={() => onDownload(j.id)}
                  onRegenerate={() => onRegenerate(j.id)}
                  onDelete={() => onDelete(j.id)}
                  onShare={(emails) => onShare(j.id, emails)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+72px)] z-30 px-4 sm:hidden">
        <Sheet open={openCreate} onOpenChange={setOpenCreate}>
          <SheetTrigger asChild>
            <Button size="lg" className="h-12 w-full shadow-xl">
              <Plus className="mr-1 h-5 w-5" /> New export
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto">
            <SheetHeader><SheetTitle>New export</SheetTitle></SheetHeader>
            <CreateExportForm
              kind={kind} setKind={setKind}
              format={format} setFormat={setFormat}
              from={from} setFrom={setFrom}
              to={to} setTo={setTo}
              siteId={siteId} setSiteId={setSiteId}
              sites={sites}
              bulk={bulk} setBulk={setBulk}
              onSubmit={() => createMutation.mutate()}
              pending={createMutation.isPending}
            />
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}

function CreateExportDialog(props: CreateProps) {
  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>New export</DialogTitle></DialogHeader>
      <CreateExportForm {...props} />
      <DialogFooter />
    </DialogContent>
  );
}

type CreateProps = {
  kind: string; setKind: (v: string) => void;
  format: string; setFormat: (v: string) => void;
  from: string; setFrom: (v: string) => void;
  to: string; setTo: (v: string) => void;
  siteId: string; setSiteId: (v: string) => void;
  sites: Site[];
  bulk: boolean; setBulk: (v: boolean) => void;
  onSubmit: () => void;
  pending: boolean;
};

function CreateExportForm(p: CreateProps) {
  return (
    <div className="space-y-4 py-2">
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Report type</Label>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {KIND_OPTIONS.map((o) => {
            const Icon = o.icon;
            const active = p.kind === o.value;
            return (
              <button key={o.value} type="button"
                onClick={() => p.setKind(o.value)}
                className={`flex h-20 flex-col items-start justify-between rounded-lg border p-2 text-left text-xs transition ${
                  active ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:bg-muted/40"
                }`}>
                <Icon className="h-4 w-4" />
                <span className="font-medium leading-tight">{o.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Format</Label>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {FORMAT_OPTIONS.map((o) => {
            const active = p.format === o.value;
            return (
              <button key={o.value} type="button"
                onClick={() => p.setFormat(o.value)}
                className={`flex flex-col rounded-lg border p-2 text-left text-xs transition ${
                  active ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:bg-muted/40"
                }`}>
                <span className="font-semibold">{o.label}</span>
                <span className="text-[10px] text-muted-foreground">{o.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs">From</Label>
          <Input aria-label="From" type="datetime-local" value={p.from} onChange={(e) => p.setFrom(e.target.value)} className="h-10" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input aria-label="To" type="datetime-local" value={p.to} onChange={(e) => p.setTo(e.target.value)} className="h-10" />
        </div>
      </div>

      <div>
        <Label className="text-xs">Site</Label>
        <Select value={p.siteId} onValueChange={p.setSiteId}>
          <SelectTrigger aria-label="Site" className="h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sites</SelectItem>
            {p.sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Button className="w-full" size="lg" onClick={p.onSubmit} disabled={p.pending}>
        {p.pending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…</>) : (<><Filter className="mr-2 h-4 w-4" /> Generate export</>)}
      </Button>
    </div>
  );
}

function ExportRow({ job, onDownload, onRegenerate, onDelete, onShare }: {
  job: any;
  onDownload: () => void;
  onRegenerate: () => void;
  onDelete: () => void;
  onShare: (emails: string[]) => void;
}) {
  const [shareOpen, setShareOpen] = useState(false);
  const [emails, setEmails] = useState("");
  const ready = job.status === "ready";
  const failed = job.status === "failed";
  const expired = job.status === "expired";

  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
          failed ? "bg-destructive/15 text-destructive"
            : ready ? "bg-primary/15 text-primary"
            : "bg-muted text-muted-foreground"
        }`}>
          {job.format === "csv" || job.format === "xlsx" ? <FileSpreadsheet className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-medium">{titleFor(job.kind)}</span>
            <Badge variant="outline" className="text-[10px] uppercase">{job.format}</Badge>
            <Badge variant={ready ? "secondary" : failed ? "destructive" : "outline"} className="text-[10px] capitalize">
              {job.status}
            </Badge>
            {job.shared_with?.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <Mail className="h-3 w-3" /> {job.shared_with.length}
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-muted-foreground">
            <span>{new Date(job.created_at).toLocaleString()}</span>
            {job.row_count != null && <span>{job.row_count} rows</span>}
            {job.file_size_bytes != null && <span>{prettyBytes(job.file_size_bytes)}</span>}
            {job.expires_at && <span>Expires {new Date(job.expires_at).toLocaleDateString()}</span>}
          </div>
          {failed && job.error_message && (
            <div className="mt-1 text-[11px] text-destructive">{job.error_message}</div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        <Button size="sm" variant="outline" onClick={onDownload} disabled={!ready}>
          <Download className="mr-1 h-3.5 w-3.5" /> Download
        </Button>
        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={!ready}>
              <Share2 className="mr-1 h-3.5 w-3.5" /> Share
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Share export</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <Label className="text-xs">Recipient emails (comma-separated)</Label>
              <Input aria-label="Recipient emails (comma-separated)" value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="name@example.com, ..." />
            </div>
            <DialogFooter>
              <Button onClick={() => {
                const list = emails.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
                if (list.length === 0) return toast.error("Add at least one email");
                onShare(list); setEmails(""); setShareOpen(false);
              }}>Send</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Button size="sm" variant="outline" onClick={onRegenerate} title={expired ? "Regenerate expired export" : "Regenerate"}>
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> {expired ? "Regenerate" : "Refresh"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      </div>
    </li>
  );
}

function titleFor(kind: string) {
  const m = KIND_OPTIONS.find((o) => o.value === kind);
  return m?.label ?? kind;
}

function prettyBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
